import React, { useState } from 'react';
import { FlagGroup } from '@atlaskit/flag';
import { ButtonItem } from '@atlaskit/menu';
import { reportFeedback } from '@atlassian/temp-proxies';
import { useApplicationContext } from '../../common-context';
import { NotificationFlag, NotificationFlagType } from '../../notification-flag';
import { PanelTypeEnum } from '../../error-page/src/constants';
import { AddScreenshot } from '../../add-screenshot-util/src/add-screenshot-util';
import { FeedbackForm } from '../../feedback-form';
import { FeedbackTypeEnum } from '../../feedback-form/src/constants';
import { FlagTypeEnum } from '../../constants';

const FeedbackTitle = 'Send us your feedback';
const summaryPlaceholderText = 'A summary of your feedback in few words.';
const descriptionPlaceholderText = 'A brief description of the feedback you would like to share.';
const checkbox1LabelText = 'Include automatic snapshot of the error to help with reporting';
const checkbox2LabelText = 'Automatically create a CCPS ticket for this issue';
const successFlagMessage = 'We appreciate your input and have passed on your thoughts';
const errorFlagMessage = 'There was an error while submitting your feedback. Please try again later.';

interface Payload {
  userName: string;
  userEmail: string;
  AAID: string;
  userInput: string;
  appName: string;
  errorPageURL: string;
  type?: string;
  shouldCreateSupportTicket?: boolean;
  summary?: string;
  feedbackType?: string;
}

export const options = [
  { label: 'Report an issue', value: FeedbackTypeEnum.BUG },
  { label: 'Suggest an improvement', value: FeedbackTypeEnum.SUGGESTION },
  { label: 'Leave a comment', value: FeedbackTypeEnum.COMMENT },
  { label: 'Ask a question', value: FeedbackTypeEnum.QUESTION },
];

const FeedbackPreamble = (props: { chatRoomLink: string; chatRoomName: string }) => {
  return (
    <p>
      We want to hear from you. Below feedback will be sent to{' '}
      <a href={props.chatRoomLink} target="_blank" rel="noopener noreferrer">
        {props.chatRoomName}
      </a>
    </p>
  );
};

export const FeedbackReceiver = (props: {
  chatRoomLink: string;
  chatRoomName: string;
  enableTicketCreationOption?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayFlag, setDisplayFlag] = useState(false);
  const [flagType, setFlagType] = useState<NotificationFlagType>(FlagTypeEnum.SUCCESS);
  const [files, setFiles] = useState<File[] | undefined>(undefined);

  const open = () => setIsOpen(true);
  const close = () => {
    setIsOpen(false);
    setFiles(undefined);
  };
  const showFlag = () => setDisplayFlag(true);
  const hideFlag = () => setDisplayFlag(false);
  const context = useApplicationContext();

  const handleFileChange = (files: File[]) => {
    setFiles(files);
  };

  const submitForm = async (data: {
    optionSelected?: string;
    description: string;
    summary?: string;
    additionalOption2?: boolean;
  }) => {
    const { optionSelected: feedbackType, description, summary, additionalOption2: shouldCreateSupportTicket } = data;
    if (context) {
      const payload: Payload = {
        userName: context.displayName,
        userEmail: context.email,
        AAID: context.atlassianAccountId,
        userInput: description,
        appName: context.product,
        errorPageURL: window.location.href,
        type: PanelTypeEnum.FEEDBACK,
        shouldCreateSupportTicket: shouldCreateSupportTicket,
        feedbackType: feedbackType,
      };

      if (feedbackType === FeedbackTypeEnum.BUG) {
        payload.summary = summary;
      }
      const response = await reportFeedback(payload, files);
      response === 200 ? setFlagType(FlagTypeEnum.SUCCESS) : setFlagType(FlagTypeEnum.ERROR);
      close();
      showFlag();
    }
  };

  const handlePropagation = (e: React.FormEvent<HTMLFormElement>) => {
    // To prevent submitting the parent form added stop propagation here
    // for example in change subscription pane it is getting submitted.
    e.stopPropagation();
  };

  return (
    <div
      onClick={(e) => {
        // added stopPropagation because clicking anywhere on the modal was closing it due to the
        // onClose getting triggered for the open popup dropdown in PPC navigation bar profile button
        e.stopPropagation();
      }}
      className="reporting-container"
    >
      <ButtonItem onClick={open} data-testid="feedback-icon">
        Give feedback
      </ButtonItem>
      {isOpen && (
        <form onSubmit={handlePropagation}>
          <FeedbackForm
            onClose={close}
            onSubmit={submitForm}
            feedbackTitle={FeedbackTitle}
            feedbackTitleDetails={
              <FeedbackPreamble chatRoomLink={props.chatRoomLink} chatRoomName={props.chatRoomName} />
            }
            showCheckbox2={props.enableTicketCreationOption}
            summaryPlaceholder={summaryPlaceholderText}
            additionalOptionLabel1={checkbox1LabelText}
            additionalOptionLabel2={checkbox2LabelText}
            descriptionPlaceholder={descriptionPlaceholderText}
            submitButtonLabel={'Send'}
            cancelButtonLabel={'Cancel'}
            customContent={<AddScreenshot onFileSelected={handleFileChange} />}
            feedbackGroupLabels={options}
            showTypeField={true}
          />
        </form>
      )}
      <FlagGroup onDismissed={hideFlag}>
        {displayFlag && (
          <NotificationFlag
            id="feedback"
            header={flagType === FlagTypeEnum.SUCCESS ? 'Thanks for your feedback' : 'Something went wrong'}
            type={flagType}
            message={flagType === FlagTypeEnum.SUCCESS ? successFlagMessage : errorFlagMessage}
          />
        )}
      </FlagGroup>
    </div>
  );
};
